import React, { useCallback, useEffect, useState } from "react";
import { FaCheckCircle, FaCopy, FaExternalLinkAlt, FaPause, FaPlay, FaPlus, FaShopify, FaTrash, FaVial } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../utils/axiosInstance";
import { useModal } from "../context/ModalContext";
import { Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Input, InputGroup, PageHeader, Select, Spinner } from "../components/ui";

const TOPICS = [
  { value: "orders/create", label: "Order created", shopifyLabel: "Order creation" },
  { value: "orders/paid", label: "Order paid", shopifyLabel: "Order payment" },
  { value: "orders/fulfilled", label: "Order fulfilled", shopifyLabel: "Order fulfillment" },
  { value: "orders/cancelled", label: "Order cancelled", shopifyLabel: "Order cancellation" },
];

const initialForm = { name: "My Shopify store", shopDomain: "", signingSecret: "" };

function formatDate(value) {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
}

export default function ShopifyIntegration() {
  const modal = useModal();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState("");
  const [events, setEvents] = useState({});
  const [flowForm, setFlowForm] = useState({});
  const [testNumbers, setTestNumbers] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [connectionRes, sessionRes] = await Promise.all([
        api.get("/api/integrations/shopify"),
        api.get("/api/sessions"),
      ]);
      const connectedSessions = (sessionRes.data?.data || sessionRes.data || []).filter((session) => session.status === "connected");
      const rows = connectionRes.data?.data || [];
      setConnections(rows);
      setSessions(connectedSessions);
      setFlowForm((current) => {
        const next = { ...current };
        rows.forEach((row) => {
          next[row.id] ||= { sessionId: connectedSessions[0]?.sessionId || "", topic: "orders/create" };
        });
        return next;
      });
    } catch (error) {
      modal.alert(error.response?.data?.message || "Failed to load Shopify connections", "error");
    } finally {
      setLoading(false);
    }
  }, [modal]);

  useEffect(() => { load(); }, [load]);

  const createConnection = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/integrations/shopify", form);
      setForm(initialForm);
      setShowForm(false);
      modal.alert("Shopify webhook connection created", "success");
      await load();
    } catch (error) {
      modal.alert(error.response?.data?.message || "Failed to create Shopify connection", "error");
    } finally {
      setSaving(false);
    }
  };

  const copy = async (value, label) => {
    await navigator.clipboard.writeText(value);
    modal.alert(`${label} copied`, "success");
  };

  const toggle = async (connection) => {
    const status = connection.status === "connected" ? "paused" : "connected";
    try {
      await api.patch(`/api/integrations/shopify/${connection.id}`, { status });
      await load();
    } catch (error) {
      modal.alert(error.response?.data?.message || "Failed to update connection", "error");
    }
  };

  const remove = async (connection) => {
    const confirmed = await modal.confirm(
      `Delete ${connection.name}? Any flow using it will be deactivated. Remove the matching webhooks in Shopify too.`,
      { danger: true, confirmText: "Delete connection" }
    );
    if (!confirmed) return;
    try {
      await api.delete(`/api/integrations/shopify/${connection.id}`);
      await load();
    } catch (error) {
      modal.alert(error.response?.data?.message || "Failed to delete connection", "error");
    }
  };

  const fetchEvents = async (connectionId) => {
    try {
      const response = await api.get(`/api/integrations/shopify/${connectionId}/events`);
      setEvents((current) => ({ ...current, [connectionId]: response.data?.data || [] }));
    } catch {
      setEvents((current) => ({ ...current, [connectionId]: [] }));
    }
  };

  const loadEvents = async (connectionId) => {
    if (expandedId === connectionId) {
      setExpandedId("");
      return;
    }
    setExpandedId(connectionId);
    if (!events[connectionId]) await fetchEvents(connectionId);
  };

  const createFlow = async (connection) => {
    const values = flowForm[connection.id] || {};
    if (!values.sessionId) return modal.alert("Connect a WhatsApp number first", "warning");
    try {
      await api.post(`/api/integrations/shopify/${connection.id}/create-flow`, values);
      modal.alert("Draft Shopify flow created. Review and activate it in Flow Builder.", "success");
      navigate("/flow-builder");
    } catch (error) {
      modal.alert(error.response?.data?.message || "Failed to create flow", "error");
    }
  };

  const sendTest = async (connection) => {
    const recipient = testNumbers[connection.id] || "";
    const topic = flowForm[connection.id]?.topic || "orders/create";
    try {
      const response = await api.post(`/api/integrations/shopify/${connection.id}/test`, { recipient, topic });
      if (!response.data?.success) throw new Error(response.data?.message || "Test was not processed");
      modal.alert("Test event processed by the active Shopify flow", "success");
      await fetchEvents(connection.id);
      setExpandedId(connection.id);
    } catch (error) {
      modal.alert(error.response?.data?.message || error.message || "Test failed", "error");
    }
  };

  return (
    <div className="page-content">
      <PageHeader title="Shopify Integration" subtitle="Start native Walytic flows from signed Shopify order webhooks">
        <Button onClick={() => setShowForm((value) => !value)}><FaPlus size={11} /> Add store</Button>
      </PageHeader>

      <Alert variant="info" className="mb-5">
        This connection uses Shopify's store webhooks and does not require Zapier, Make, Shopify Flow, or an App Store-approved Shopify app.
      </Alert>

      {showForm && (
        <Card className="mb-6">
          <CardHeader><h2 className="text-sm font-semibold text-ink-primary">Add a Shopify store</h2></CardHeader>
          <CardBody>
            <form onSubmit={createConnection} className="grid gap-4 md:grid-cols-3">
              <InputGroup label="Connection name"><Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></InputGroup>
              <InputGroup label="Store domain" hint="Example: my-store.myshopify.com"><Input value={form.shopDomain} onChange={(event) => setForm({ ...form, shopDomain: event.target.value })} placeholder="my-store.myshopify.com" required /></InputGroup>
              <InputGroup label="Shopify webhook signing secret" hint="Shown in Shopify's Webhooks settings. Stored encrypted and never displayed again."><Input type="password" value={form.signingSecret} onChange={(event) => setForm({ ...form, signingSecret: event.target.value })} autoComplete="new-password" required /></InputGroup>
              <div className="md:col-span-3 flex justify-end gap-2"><Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button><Button type="submit" loading={saving}>Create connection</Button></div>
            </form>
          </CardBody>
        </Card>
      )}

      {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : connections.length === 0 ? (
        <div className="text-center">
          <EmptyState icon={FaShopify} title="No Shopify stores connected" description="Add a store to generate its secure Walytic webhook endpoint." />
          <Button onClick={() => setShowForm(true)}>Add Shopify store</Button>
        </div>
      ) : (
        <div className="space-y-5">
          {connections.map((connection) => {
            const values = flowForm[connection.id] || { sessionId: "", topic: "orders/create" };
            const connectionEvents = events[connection.id];
            return (
              <Card key={connection.id}>
                <CardHeader className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3"><span className="h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><FaShopify /></span><div><h2 className="text-sm font-semibold text-ink-primary">{connection.name}</h2><p className="text-xs text-ink-muted">{connection.shopDomain}</p></div></div>
                  <div className="flex items-center gap-2"><Badge variant={connection.status === "connected" ? "success" : "neutral"}>{connection.status}</Badge><Button size="sm" variant="ghost" onClick={() => toggle(connection)}>{connection.status === "connected" ? <FaPause size={10} /> : <FaPlay size={10} />}{connection.status === "connected" ? "Pause" : "Resume"}</Button><Button size="sm" variant="danger" onClick={() => remove(connection)}><FaTrash size={10} /></Button></div>
                </CardHeader>
                <CardBody className="space-y-5">
                  <div>
                    <label className="input-label">Walytic webhook URL</label>
                    <div className="flex gap-2"><Input value={connection.endpoint} readOnly className="font-mono text-xs" /><Button variant="secondary" onClick={() => copy(connection.endpoint, "Webhook URL")}><FaCopy size={11} /> Copy</Button></div>
                  </div>

                  <div className="rounded-lg border border-surface-border bg-surface-bg p-4">
                    <h3 className="text-sm font-semibold text-ink-primary mb-2">Configure Shopify</h3>
                    <ol className="list-decimal pl-5 text-xs text-ink-muted space-y-1.5">
                      <li>Open Shopify Admin → Settings → Notifications → Webhooks.</li>
                      <li>Create a webhook, select an order event, choose JSON, and paste the URL above.</li>
                      <li>Repeat for each event your Walytic flows use. Every event uses the same URL.</li>
                      <li>Use Shopify's Send test action, then confirm it appears in Event history below.</li>
                    </ol>
                    <a href="https://help.shopify.com/en/manual/fulfillment/setup/notifications/webhooks" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs text-brand hover:underline">Shopify webhook instructions <FaExternalLinkAlt size={9} /></a>
                  </div>

                  <div className="grid gap-3 lg:grid-cols-[1fr_1fr_auto] items-end">
                    <InputGroup label="WhatsApp connection"><Select value={values.sessionId} onChange={(event) => setFlowForm({ ...flowForm, [connection.id]: { ...values, sessionId: event.target.value } })}><option value="">Choose a connected number</option>{sessions.map((session) => <option key={session.sessionId} value={session.sessionId}>{session.number || session.sessionId} ({session.transportType === "walytic_cloud" ? "Cloud" : "Web"})</option>)}</Select></InputGroup>
                    <InputGroup label="Shopify event"><Select value={values.topic} onChange={(event) => setFlowForm({ ...flowForm, [connection.id]: { ...values, topic: event.target.value } })}>{TOPICS.map((topic) => <option key={topic.value} value={topic.value}>{topic.label}</option>)}</Select></InputGroup>
                    <Button onClick={() => createFlow(connection)}>Create draft flow</Button>
                  </div>

                  <div className="grid gap-3 lg:grid-cols-[1fr_auto] items-end">
                    <InputGroup label="Test WhatsApp number" hint="Uses a synthetic Shopify order and requires an active matching flow."><Input value={testNumbers[connection.id] || ""} onChange={(event) => setTestNumbers({ ...testNumbers, [connection.id]: event.target.value })} placeholder="Country code + number" /></InputGroup>
                    <Button variant="secondary" onClick={() => sendTest(connection)}><FaVial size={11} /> Send test event</Button>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-ink-muted"><span>{connection.flowCount} flow{connection.flowCount === 1 ? "" : "s"} · Last webhook: {formatDate(connection.lastWebhookAt)}</span><button className="text-brand hover:underline" onClick={() => loadEvents(connection.id)}>{expandedId === connection.id ? "Hide event history" : "View event history"}</button></div>
                  {expandedId === connection.id && (
                    <div className="overflow-x-auto border border-surface-border rounded-lg">
                      {!connectionEvents ? <div className="p-5 flex justify-center"><Spinner size="sm" /></div> : connectionEvents.length === 0 ? <p className="p-5 text-xs text-ink-muted">No Shopify events received yet.</p> : (
                        <table className="w-full text-xs"><thead><tr className="bg-surface-bg text-left text-ink-muted"><th className="p-3">Event</th><th className="p-3">Order</th><th className="p-3">Status</th><th className="p-3">Received</th><th className="p-3">Result</th></tr></thead><tbody>{connectionEvents.map((item) => <tr key={item.id} className="border-t border-surface-border"><td className="p-3 font-mono">{item.topic}</td><td className="p-3">{item.orderName || item.orderId || "—"}</td><td className="p-3"><Badge variant={item.status === "processed" ? "success" : item.status === "failed" ? "danger" : "neutral"}>{item.status}</Badge></td><td className="p-3 whitespace-nowrap">{formatDate(item.receivedAt)}</td><td className="p-3 text-ink-muted">{item.error || `${item.flowExecutionIds?.length || 0} flow execution(s)`}</td></tr>)}</tbody></table>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 flex gap-2"><FaCheckCircle className="mt-0.5 shrink-0" /><p>For WhatsApp Cloud connections, proactive Shopify notifications must use an approved template. Walytic Web connections can use the draft flow's free-form message.</p></div>
    </div>
  );
}
